/**
 * The parts of a custom attribution model the budget math reads. Structural so
 * the UI's CustomAttributionModelWithWeights and the API's model rows both fit.
 */
export interface AttributionModelLike {
  initiativeType?: unknown;
  adChannel?: unknown;
  adChannelObjective?: unknown;
  adChannelOptimizationGoal?: unknown;
  weights?: ReadonlyArray<{ source: string; metric: string; weight: number }> | null;
}
type CustomAttributionModelWithWeights = AttributionModelLike;

/**
 * Maps source:metric pairs to their Cube measure field names.
 * Triple Whale metrics are keyed by metric alone (source-agnostic).
 */
const SOURCE_METRIC_TO_CUBE_FIELD: Record<string, string> = {
  // Google Analytics
  'GOOGLE_ANALYTICS:LAST_CLICK_REVENUE': 'ga4_total_revenue',
  // Google Ads
  'GOOGLE_ADS:SEVEN_DAY_CLICK_1D_VIEW': 'purchases',
  // TikTok
  'TIKTOK:SEVEN_DAY_CLICK_1D_VIEW': 'purchases',
  // Pinterest (`purchases` = the account window, 30d click / 30d engagement / 1d view)
  'PINTEREST:30D_CLICK_30D_ENGAGEMENT_1D_VIEW': 'purchases',
  'PINTEREST:1D_VIEW': 'purchase_value_1d_view',
  // Meta
  'META:7D_CLICK': 'purchases',
  'META:1D_VIEW': 'purchase_value_1d_view',
  'META:1D_ENGAGED_VIEW': 'purchase_value_1d_engaged_view',
  'META:INCREMENTALITY': 'purchase_value_incrementality',
  // Shopify
  'SHOPIFY:LAST_CLICK_REVENUE': 'shopify_last_touch_revenue',
  'SHOPIFY:FIRST_CLICK_REVENUE': 'shopify_first_touch_revenue',
  // Marathon
  'MARATHON:REVENUE_FROM_SEARCH': 'revenue_from_search',
};

const TRIPLE_WHALE_METRIC_TO_CUBE_FIELD: Record<string, string> = {
  TRIPLE_WHALE_LINEAR: 'triple_whale_linear_total_revenue',
  TRIPLE_WHALE_LAST_PLATFORM_CLICK: 'triple_whale_last_platform_click_total_revenue',
  TRIPLE_WHALE_FULL_FIRST_CLICK: 'triple_whale_full_first_click_total_revenue',
  TRIPLE_WHALE_FULL_LAST_CLICK: 'triple_whale_full_last_click_total_revenue',
  TRIPLE_WHALE_LINEAR_ALL: 'triple_whale_linear_all_total_revenue',
  TRIPLE_WHALE_LINEAR_NC: 'triple_whale_linear_nc_total_revenue',
  TRIPLE_WHALE_LAST_PLATFORM_CLICK_NC: 'triple_whale_last_platform_click_nc_total_revenue',
  TRIPLE_WHALE_FULL_FIRST_CLICK_NC: 'triple_whale_full_first_click_nc_total_revenue',
  TRIPLE_WHALE_FULL_LAST_CLICK_NC: 'triple_whale_full_last_click_nc_total_revenue',
  TRIPLE_WHALE_LINEAR_ALL_NC: 'triple_whale_linear_all_nc_total_revenue',
};

function getCubeFieldForWeight(source: string, metric: string): string | null {
  if (metric.startsWith('TRIPLE_WHALE_')) {
    return TRIPLE_WHALE_METRIC_TO_CUBE_FIELD[metric] ?? null;
  }
  return SOURCE_METRIC_TO_CUBE_FIELD[`${source}:${metric}`] ?? null;
}

function readCubeValue(rowData: Record<string, unknown>, cubeField: string): number {
  const raw = (rowData[cubeField] || rowData[`company_media_metrics_adsets.${cubeField}`] || '0') as string;
  return parseFloat(raw);
}

// Brand value calculation types
export interface BrandValueBreakdown {
  '30d': number;
  '60d': number;
  '90d': number;
  '180d': number;
  total: number;
}

export function calculateModeledRevenueForRow({
  rowData,
  model,
}: {
  rowData: Record<string, unknown>;
  model: CustomAttributionModelWithWeights | undefined;
  adChannel?: string | null;
}): number {
  if (!rowData || !model) return 0;

  let total = 0;

  for (const w of model.weights || []) {
    // Brand value metrics are handled by calculateBrandValueBreakdownForRow
    if (w.metric.startsWith('BRAND_VALUE_')) continue;
    if (w.weight === 0) continue;

    const cubeField = getCubeFieldForWeight(w.source, w.metric);
    if (!cubeField) continue;

    total += readCubeValue(rowData, cubeField) * w.weight;
  }

  return total;
}

// Helper functions for brand value calculations
export function calculateBrandValueBreakdownForRow({
  rowData,
  model,
}: {
  rowData: Record<string, unknown>;
  model: CustomAttributionModelWithWeights | undefined;
}): BrandValueBreakdown {
  const result: BrandValueBreakdown = {
    '30d': 0,
    '60d': 0,
    '90d': 0,
    '180d': 0,
    total: 0,
  };

  if (!rowData) return result;

  // Check if the model has explicit brand value weights configured
  const hasBrandValueMetrics = model?.weights?.some((w) => w.metric.startsWith('BRAND_VALUE_')) ?? false;

  if (!model || !hasBrandValueMetrics) {
    // No model or no brand value weights defined — fallback to brand_value_agg * 1.0
    result['180d'] = parseFloat((rowData.brand_value_agg || rowData['company_media_metrics_adsets.brand_value_agg'] || '0') as string);
    result.total = result['180d'];
    return result;
  }

  const weightMap = Object.fromEntries((model.weights || []).map((w) => [w.metric, w.weight]));

  result['30d'] =
    parseFloat((rowData.d30_brand_value || rowData['company_media_metrics_adsets.d30_brand_value'] || '0') as string) *
    (weightMap['BRAND_VALUE_30D'] ?? 0);

  result['60d'] =
    parseFloat((rowData.d60_brand_value || rowData['company_media_metrics_adsets.d60_brand_value'] || '0') as string) *
    (weightMap['BRAND_VALUE_60D'] ?? 0);

  result['90d'] =
    parseFloat((rowData.d90_brand_value || rowData['company_media_metrics_adsets.d90_brand_value'] || '0') as string) *
    (weightMap['BRAND_VALUE_90D'] ?? 0);

  result['180d'] =
    parseFloat((rowData.brand_value_agg || rowData['company_media_metrics_adsets.brand_value_agg'] || '0') as string) *
    (weightMap['BRAND_VALUE_180D'] ?? 0);

  result.total = result['30d'] + result['60d'] + result['90d'] + result['180d'];

  return result;
}

export function getCurrentBrandValueBreakdown(rowData: Record<string, unknown>): BrandValueBreakdown {
  const result: BrandValueBreakdown = {
    '30d': 0,
    '60d': 0,
    '90d': 0,
    '180d': 0,
    total: 0,
  };

  if (!rowData) return result;

  result['30d'] = parseFloat((rowData.d30_brand_value || rowData['company_media_metrics_adsets.d30_brand_value'] || '0') as string);

  result['60d'] = parseFloat((rowData.d60_brand_value || rowData['company_media_metrics_adsets.d60_brand_value'] || '0') as string);

  result['90d'] = parseFloat((rowData.d90_brand_value || rowData['company_media_metrics_adsets.d90_brand_value'] || '0') as string);

  result['180d'] = parseFloat((rowData.brand_value_agg || rowData['company_media_metrics_adsets.brand_value_agg'] || '0') as string);

  result.total = result['30d'] + result['60d'] + result['90d'] + result['180d'];

  return result;
}

export function getBrandValuePeriodsWithWeights(model: CustomAttributionModelWithWeights | undefined): string[] {
  if (!model?.weights) return [];

  const periods: string[] = [];
  const weightMap = Object.fromEntries((model.weights || []).map((w) => [w.metric, w.weight]));

  if ((weightMap['BRAND_VALUE_30D'] ?? 0) > 0) periods.push('30d');
  if ((weightMap['BRAND_VALUE_60D'] ?? 0) > 0) periods.push('60d');
  if ((weightMap['BRAND_VALUE_90D'] ?? 0) > 0) periods.push('90d');
  if ((weightMap['BRAND_VALUE_180D'] ?? 0) > 0) periods.push('180d');

  return periods;
}

export function hasBrandValueWeights(model: CustomAttributionModelWithWeights | undefined): boolean {
  if (!model?.weights) return false;

  return model.weights.some((weight) => weight.metric.startsWith('BRAND_VALUE_') && weight.weight > 0);
}

export function calculateBrandValueForRow({
  rowData,
  model,
  period,
}: {
  rowData: Record<string, unknown>;
  model: CustomAttributionModelWithWeights | undefined;
  period: '30d' | '60d' | '90d' | '120d' | '150d' | 'agg';
}): {
  current: number;
  estimated: number;
  next: number;
} {
  if (!rowData) {
    return { current: 0, estimated: 0, next: 0 };
  }

  // Get field names based on period
  const getFieldNames = (period: string) => {
    switch (period) {
      case '30d':
        return {
          current: 'd30_brand_value',
          estimated: 'estimated_d30_brand_value',
        };
      case '60d':
        return {
          current: 'd60_brand_value',
          estimated: 'estimated_d60_brand_value',
        };
      case '90d':
        return {
          current: 'd90_brand_value',
          estimated: 'estimated_d90_brand_value',
        };
      case '120d':
        return {
          current: 'd120_brand_value',
          estimated: 'estimated_d120_brand_value',
        };
      case '150d':
        return {
          current: 'd150_brand_value',
          estimated: 'estimated_d150_brand_value',
        };
      case 'agg':
        return {
          current: 'brand_value_agg',
          estimated: 'estimated_d180_brand_value',
        };
      default:
        return {
          current: 'd30_brand_value',
          estimated: 'estimated_d30_brand_value',
        };
    }
  };

  const fieldNames = getFieldNames(period);

  // Extract values from row data
  const current = parseFloat(
    (rowData[fieldNames.current] || rowData[`company_media_metrics_adsets.${fieldNames.current}`] || '0') as string,
  );

  const estimated = parseFloat(
    (rowData[fieldNames.estimated] || rowData[`company_media_metrics_adsets.${fieldNames.estimated}`] || '0') as string,
  );

  // Calculate next value using attribution model weights
  let next = current;

  if (model && model.weights && model.weights.length > 0) {
    // Only apply weighting if the model has explicit brand value weights configured.
    // If the model exists but has no BRAND_VALUE_* weights, fall back to 1.0 (passthrough).
    const hasBrandValueMetrics = model.weights.some((w) => w.metric.startsWith('BRAND_VALUE_'));

    if (hasBrandValueMetrics) {
      const weightMap = Object.fromEntries((model.weights || []).map((w) => [w.metric, w.weight]));

      // Get the appropriate weight key based on period
      const getWeightKey = (period: string) => {
        switch (period) {
          case '30d':
            return 'BRAND_VALUE_30D';
          case '60d':
            return 'BRAND_VALUE_60D';
          case '90d':
            return 'BRAND_VALUE_90D';
          case '120d':
            return 'BRAND_VALUE_120D';
          case '150d':
            return 'BRAND_VALUE_150D';
          case 'agg':
            return 'BRAND_VALUE_180D';
          default:
            return 'BRAND_VALUE_30D';
        }
      };

      const weightKey = getWeightKey(period);
      const weight = weightMap[weightKey] ?? 0;

      // Apply the weight to calculate next value
      next = current * weight;
    }
  }

  return {
    current: isFinite(current) ? current : 0,
    estimated: isFinite(estimated) ? estimated : 0,
    next: isFinite(next) ? next : 0,
  };
}
